import React, { createContext, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

const PageHeaderContext = createContext({
  titleOverride: null,
  setTitleOverride: () => {},
  summary: null,
  setSummary: () => {},
  // 🔑 The default matters: `pages/OnboardingCreateProduction.js` is rendered
  // OUTSIDE `PageHeaderProvider` (App.js) and calls `usePageViewLogger()`, so
  // the logger reads this object rather than a provider. It must be a real
  // shape, not `undefined`.
  publishedView: null,
  setPublishedView: () => {},
});

export function PageHeaderProvider({ children }) {
  const [titleOverride, setTitleOverride] = useState(null);
  const [summary, setSummary] = useState(null);
  const [publishedView, setPublishedView] = useState(null);
  return (
    <PageHeaderContext.Provider
      value={{
        titleOverride, setTitleOverride, summary, setSummary, publishedView, setPublishedView,
      }}
    >
      {children}
    </PageHeaderContext.Provider>
  );
}

export function usePageHeader() {
  return useContext(PageHeaderContext);
}

// Sets the Layout page-title override for the lifetime of the calling
// component. Pass a falsy value to fall back to the default nav-label title.
export function usePageTitle(title) {
  const { setTitleOverride } = usePageHeader();
  useEffect(() => {
    setTitleOverride(title || null);
    return () => setTitleOverride(null);
  }, [title, setTitleOverride]);
}

// Sets a per-page summary line rendered inline next to the title in the
// Layout sticky bar (e.g. "· 2 teams · 8 tasks · 4 done"). Pass a falsy
// value to clear. Lifetime is scoped to the calling component.
export function usePageSummary(summary) {
  const { setSummary } = usePageHeader();
  useEffect(() => {
    setSummary(summary || null);
    return () => setSummary(null);
  }, [summary, setSummary]);
}

/**
 * Publish this page's CURRENT VIEW ID so the page-view logger can record WHICH
 * TAB the user is looking at, not merely which page.
 *
 * Owner, 2026-09-17: *"I would like page visits to also be included in activity
 * so I can tell if they are visiting which pagges, which tabs etc."*
 *
 * 🔑 IT IS THE THIRD `usePage*` OF THIS FILE ON PURPOSE, NOT A NEW MECHANISM.
 * `usePageTitle` and `usePageSummary` already publish from a page up to the
 * chrome; a second channel for the same shape of fact would be two things to
 * keep in step. **The logger stays the only writer of a row** — this publishes
 * a value, it does not log anything.
 *
 * 🪤 IT PUBLISHES THE PATHNAME ALONGSIDE THE VIEW ID, AND THAT IS LOAD-BEARING.
 * On a navigation React runs the OLD page's cleanup and the NEW page's effect in
 * the same pass as the logger's effect, so for one render the logger can see the
 * page it is now on paired with the view the PREVIOUS page published. Carrying
 * the path makes the logger able to reject that pairing outright, instead of
 * relying on the debounce to swallow a wrong row it already scheduled.
 *
 * ⚠️ The value is a plain view id (`'to-receive'`), never free text — what the
 * logger will actually STORE is bounded by `constants/pageViewParams.js`, which
 * drops anything outside a declared vocabulary.
 */
export function usePageView(viewId) {
  const { setPublishedView } = usePageHeader();
  const { pathname } = useLocation();
  useEffect(() => {
    if (viewId == null || viewId === '') {
      setPublishedView(null);
    } else {
      setPublishedView({ path: pathname, viewId: String(viewId) });
    }
    return () => setPublishedView(null);
  }, [viewId, pathname, setPublishedView]);
}
